import { useState, useEffect } from "react";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";

interface Project {
  id: number;
  client_id: string;
  title: string;
  description: string;
  budget: string;
  category: string;
  status: string;
  review_status: string;
  created_at: string;
  profiles: {
    email: string | null;
    username: string | null;
    display_name: string | null;
  } | null;
}

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [statusUpdateId, setStatusUpdateId] = useState<number | null>(null);
  const [newStatus, setNewStatus] = useState("");

  // Approval / Rejection states
  const [reviewActionId, setReviewActionId] = useState<number | null>(null);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewStatusMsg, setReviewStatusMsg] = useState("");

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("projects")
        .select("*, profiles:client_id(email, username, display_name)")
        .order("created_at", { ascending: false });
      setProjects(data || []);
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusUpdateId || !newStatus) return;
    try {
      const supabase = getSupabase();
      await supabase.from("projects").update({ status: newStatus }).eq("id", statusUpdateId);
      setProjects((prev) =>
        prev.map((p) => (p.id === statusUpdateId ? { ...p, status: newStatus } : p))
      );
      setStatusUpdateId(null);
      setNewStatus("");
    } catch {
      // fallback
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const supabase = getSupabase();
      await supabase.from("projects").delete().eq("id", id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setConfirmDeleteId(null);
    } catch {
      // fallback
    }
  };

  const handleReviewAction = async () => {
    if (!reviewActionId || !reviewAction) return;
    setReviewSubmitting(true);
    setReviewStatusMsg("");

    const newReviewStatus = reviewAction === "approve" ? "approved" : "rejected";

    try {
      const supabase = getSupabase();

      const { error } = await supabase
        .from("projects")
        .update({ review_status: newReviewStatus })
        .eq("id", reviewActionId);

      if (error) {
        setReviewStatusMsg("Failed to update review status.");
        setReviewSubmitting(false);
        return;
      }

      // Find the project to get user email for notification
      const project = projects.find((p) => p.id === reviewActionId);

      // Send email notification + create in-app notification for the project owner
      if (project?.profiles?.email) {
        supabase.functions.invoke("project-approve-notify", {
          body: {
            email: project.profiles.email,
            project_title: project.title,
            project_category: project.category,
            review_status: newReviewStatus,
            rejection_reason: reviewAction === "reject" ? rejectionReason : "",
            user_id: project.client_id,
            project_id: project.id,
          },
        }).catch(() => {});
      }

      // Update local state
      setProjects((prev) =>
        prev.map((p) =>
          p.id === reviewActionId ? { ...p, review_status: newReviewStatus } : p
        )
      );

      setReviewStatusMsg(
        reviewAction === "approve"
          ? "Project approved and notification sent!"
          : "Project rejected and notification sent."
      );

      // Close modal after short delay
      setTimeout(() => {
        setReviewActionId(null);
        setReviewAction(null);
        setRejectionReason("");
        setReviewStatusMsg("");
      }, 2000);
    } catch {
      setReviewStatusMsg("Something went wrong.");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const openReviewModal = (id: number, action: "approve" | "reject") => {
    setReviewActionId(id);
    setReviewAction(action);
    setRejectionReason("");
    setReviewStatusMsg("");
  };

  const filteredProjects = projects.filter((p) => {
    const matchesSearch = p.title.toLowerCase().includes(search.toLowerCase()) ||
      (p.category || "").toLowerCase().includes(search.toLowerCase());
    const matchesReview = !reviewFilter || p.review_status === reviewFilter;
    return matchesSearch && matchesReview;
  });

  const pendingCount = projects.filter((p) => p.review_status === "pending").length;

  const reviewBadge = (status: string) => {
    switch (status) {
      case "approved": return "bg-green-900/30 text-green-400";
      case "rejected": return "bg-red-900/30 text-red-400";
      case "pending": return "bg-amber-900/30 text-amber-400";
      default: return "bg-[#1c2128] text-gray-400";
    }
  };

  const projectStatusBadge = (status: string) => {
    switch (status) {
      case "open": return "bg-green-900/30 text-green-400";
      case "in_progress": return "bg-amber-900/30 text-amber-400";
      case "completed": return "bg-blue-100 text-blue-700";
      case "closed": return "bg-red-900/30 text-red-400";
      default: return "bg-[#1c2128] text-gray-400";
    }
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="text-lg font-bold text-gray-200">Projects</h1>
            <p className="text-xs text-gray-500">
              {projects.length} total ·{" "}
              <span className="text-amber-400 font-semibold">{pendingCount} pending review</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
              className="px-3 py-2 border border-gray-700 rounded-lg text-xs text-gray-500 focus:outline-none focus:border-[#5cb85c]"
            >
              <option value="">All Reviews</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search projects..."
                className="pl-9 pr-4 py-2 border border-gray-700 rounded-lg text-xs w-48 focus:outline-none focus:border-[#5cb85c]"
              />
            </div>
          </div>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
            </div>
          ) : (
            <div className="bg-[#0d1117] rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-gray-800">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Project</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Category</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Budget</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Review</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Posted</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase w-52">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredProjects.map((p) => (
                      <tr key={p.id} className="hover:bg-[#161b22]/50">
                        <td className="px-5 py-3.5">
                          <p className="text-sm font-semibold text-gray-200 max-w-[180px] truncate">{p.title}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            by {p.profiles?.username || p.profiles?.display_name || "Anonymous"}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-xs text-gray-500">{p.category || "—"}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-xs font-semibold text-gray-300">{p.budget || "—"}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${reviewBadge(p.review_status)}`}>
                            {p.review_status || "pending"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${projectStatusBadge(p.status)}`}>
                            {(p.status || "open").replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <span className="text-xs text-gray-400">
                            {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {p.review_status === "pending" && (
                              <>
                                <button
                                  onClick={() => openReviewModal(p.id, "approve")}
                                  className="px-2.5 py-1.5 rounded-lg bg-green-900/30 border border-green-700/30 text-green-400 hover:bg-green-900/50 text-[11px] font-semibold transition-all whitespace-nowrap"
                                  title="Approve"
                                >
                                  <i className="fas fa-check text-[10px] mr-1" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => openReviewModal(p.id, "reject")}
                                  className="px-2.5 py-1.5 rounded-lg bg-red-900/30 border border-red-700/30 text-red-400 hover:bg-red-900/50 text-[11px] font-semibold transition-all whitespace-nowrap"
                                  title="Reject"
                                >
                                  <i className="fas fa-times text-[10px] mr-1" />
                                  Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => { setStatusUpdateId(p.id); setNewStatus(p.status); }}
                              className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5cb85c] transition-all"
                              title="Change Status"
                            >
                              <i className="fas fa-sync-alt text-[10px]" />
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(p.id)}
                              className="w-7 h-7 rounded-lg hover:bg-red-900/20 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredProjects.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-5 py-12 text-center text-sm text-gray-400">
                          No projects found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Status Change Modal */}
      {statusUpdateId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setStatusUpdateId(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-200 mb-4">Update Project Status</h3>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]"
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="closed">Closed</option>
            </select>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setStatusUpdateId(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={handleStatusChange} className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Review Action Modal (Approve / Reject) */}
      {reviewActionId && reviewAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setReviewActionId(null); setReviewAction(null); }}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            {reviewAction === "approve" ? (
              <>
                <div className="w-14 h-14 rounded-full bg-green-900/30 border border-green-700/30 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-check-circle text-green-400 text-xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-200 text-center mb-2">Approve Project?</h3>
                <p className="text-sm text-gray-400 text-center mb-6">
                  This project will become publicly visible and the owner will be notified via email.
                </p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-red-900/30 border border-red-700/30 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-times-circle text-red-400 text-xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-200 text-center mb-2">Reject Project?</h3>
                <p className="text-sm text-gray-400 text-center mb-4">
                  The project will not be publicly visible. Please provide a reason for the rejection.
                </p>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2.5 border border-gray-700 rounded-lg text-sm text-gray-200 bg-[#161b22] focus:outline-none focus:border-red-500 resize-none mb-2"
                />
                <p className="text-[10px] text-gray-500 text-right mb-4">{rejectionReason.length}/500</p>
              </>
            )}

            {reviewStatusMsg && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                reviewAction === "approve"
                  ? "bg-green-900/20 border border-green-700/30 text-green-400"
                  : "bg-red-900/20 border border-red-700/30 text-red-400"
              }`}>
                <i className={`fas ${reviewAction === "approve" ? "fa-check-circle" : "fa-info-circle"} mr-2`} />
                {reviewStatusMsg}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setReviewActionId(null); setReviewAction(null); setRejectionReason(""); setReviewStatusMsg(""); }}
                disabled={reviewSubmitting}
                className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReviewAction}
                disabled={reviewSubmitting || (reviewAction === "reject" && !rejectionReason.trim())}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap disabled:opacity-50 ${
                  reviewAction === "approve"
                    ? "bg-green-500 hover:bg-green-600 text-white"
                    : "bg-red-500 hover:bg-red-600 text-white"
                }`}
              >
                {reviewSubmitting ? (
                  <span><i className="fas fa-spinner fa-spin mr-2" />Processing...</span>
                ) : reviewAction === "approve" ? (
                  <span><i className="fas fa-check mr-1" />Confirm Approve</span>
                ) : (
                  <span><i className="fas fa-times mr-1" />Confirm Reject</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-200 text-center mb-2">Delete Project?</h3>
            <p className="text-xs text-gray-500 text-center mb-5">This project will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}